import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  Button,
  ClickAwayListener,
  Paper,
  Popper,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";

// third-party
import PerfectScrollbar from "react-perfect-scrollbar";

// project imports
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";

// assets
import { IconLogout } from "@tabler/icons";
import { color } from "utils/color";
import API from "api/axios";
import { toast } from "react-toastify";
import ApiRoutes from "api/endpoint";

const ProfileSection = ({ name, email, type }) => {
  const theme = useTheme();
  const customization = useSelector((state) => state.customization);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  // Change password states
  const [changePassOpen, setChangePassOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPass, setChangingPass] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      return toast.error("All fields are required");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("New password and confirm password do not match");
    }
    if (newPassword.length < 8) {
      return toast.error("Password must be at least 8 characters long");
    }

    try {
      setChangingPass(true);
      await API.patch("/user/change-password-direct", {
        newPassword
      });
      toast.success("Password changed successfully!");
      setChangePassOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      console.error("Error changing password:", err);
      toast.error(err.response?.data?.message || "Failed to change password");
    } finally {
      setChangingPass(false);
    }
  };

  const handleLogout = async () => {
    try {
      await API.request({
        url: ApiRoutes.Auth.Logout.Endpoint,
        method: ApiRoutes.Auth.Logout.Method,
      });
      toast.success("Logged out!!!");
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      localStorage.clear();
      navigate("/");
    }
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };



  const prevOpen = useRef(open);

  useEffect(() => {
    if (prevOpen.current && !open) anchorRef.current?.focus();
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Avatar
        ref={anchorRef}
        onClick={handleToggle}
        sx={{
          ...theme.typography.mediumAvatar,
          cursor: "pointer",
          backgroundColor: color.fg,
          color: color.bg,
          transition: "all .2s ease-in-out",
          "&:hover": {
            backgroundColor: "#1e88e5",
            color: "#fff",
          },
        }}
      />

      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        modifiers={[{ name: "offset", options: { offset: [0, 14] } }]}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <Paper sx={{ width: 280, borderRadius: 2, border: `1px solid ${color.bg}` }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} content={false}>
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={1}>
                      <Typography variant="h4" sx={{ textTransform: "capitalize" }}>
                        Welcome Back, {localStorage.getItem("userRoleName") || "Admin"}
                      </Typography>
                      <Typography variant="body2">{email}</Typography>
                    </Stack>
                  </Box>

                  <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Button
                      fullWidth
                      onClick={() => setChangePassOpen(true)}
                      sx={{
                        borderRadius: `${customization.borderRadius}px`,
                        border: `1.5px solid #1C252E`,
                        color: "#1C252E",
                        py: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        textTransform: "none",
                        fontWeight: 600,
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                          transform: "translateY(-1px)"
                        },
                      }}
                    >
                      Change Password
                    </Button>

                    <Button
                      fullWidth
                      onClick={handleLogout}
                      sx={{
                        borderRadius: `${customization.borderRadius}px`,
                        backgroundColor: "#EF4444",
                        color: "#ffffff",
                        py: 1.2,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 1,
                        textTransform: "none",
                        boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)",
                        transition: "all 0.3s ease-in-out",
                        "&:hover": {
                          backgroundColor: "#DC2626",
                          transform: "translateY(-2px)",
                          boxShadow: "0 4px 12px rgba(239, 68, 68, 0.5)",
                        },
                      }}
                    >
                      <IconLogout size="1.4rem" stroke={2} />
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#ffffff", ml: 1 }}>
                        Logout
                      </Typography>
                    </Button>
                  </Box>

                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
      <Dialog 
        open={changePassOpen} 
        onClose={() => setChangePassOpen(false)}
        PaperProps={{
          style: { borderRadius: 16, padding: 16, width: 400 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: "18px", color: "#1C252E", pb: 1 }}>
          Change Account Password
        </DialogTitle>
        <form onSubmit={handleChangePassword}>
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="New Password"
              type="password"
              fullWidth
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1 }}>
            <Button 
              onClick={() => setChangePassOpen(false)} 
              sx={{ color: "#637381", fontWeight: 700, textTransform: "none" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={changingPass}
              variant="contained"
              sx={{
                backgroundColor: "#00A76F",
                color: "#FFF",
                fontWeight: 700,
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": { backgroundColor: "#008F5D" }
              }}
            >
              {changingPass ? "Saving..." : "Change Password"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ProfileSection;